/**
 * Generic 24-bucket (or N-bucket) stacked-bar hourly chart. Hand-rolled SVG;
 * no JS chart deps. One bar per bucket, layers stacked bottom-to-top in the
 * order they appear in `series`. Hover surfaces exact numbers via native
 * <title> tooltips.
 *
 * Designed for the insights pages (Deliverability, Health, Cost, Engagement).
 * Each layer can be a count or a money amount — pass `valueFormatter` so the
 * tooltip renders the right units.
 */

export interface HourlySeries {
  label: string;
  /** Tailwind / arbitrary CSS color for both the legend dot and the SVG fill. */
  fill: string;
  /** One value per bucket, same length as `hours`. */
  values: number[];
}

interface Props {
  /** ISO timestamps, one per bucket, ordered oldest → newest. */
  hours: string[];
  series: HourlySeries[];
  /** Format the tooltip value (default: integer with thousand separators). */
  valueFormatter?: (n: number) => string;
  /** Format the y-axis tick label (default: integer). */
  axisFormatter?: (n: number) => string;
  /** Accessible chart description. */
  ariaLabel?: string;
  /** Default 200px. */
  height?: number;
}

export function HourlyChart({
  hours,
  series,
  valueFormatter = formatInt,
  axisFormatter = formatInt,
  ariaLabel = "Hourly chart",
  height = 200,
}: Props) {
  const W = 760;
  const H = height;
  const PADDING = { top: 16, right: 12, bottom: 28, left: 44 };
  const innerW = W - PADDING.left - PADDING.right;
  const innerH = H - PADDING.top - PADDING.bottom;
  const slot = innerW / Math.max(hours.length, 1);
  const barWidth = Math.max(slot * 0.62, 2);

  // Total per bucket = sum of every layer at that index.
  const totals = hours.map((_, i) => series.reduce((sum, s) => sum + (s.values[i] ?? 0), 0));
  const maxTotal = Math.max(1, ...totals);
  const niceMax = niceCeil(maxTotal);
  const yTicks = [0, niceMax / 2, niceMax];

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="block w-full min-w-[640px]"
        style={{ height: `${H}px` }}
        role="img"
        aria-label={ariaLabel}
      >
        {/* y-axis grid + labels */}
        {yTicks.map((v) => {
          const y = PADDING.top + innerH - (v / niceMax) * innerH;
          return (
            <g key={v}>
              <line
                x1={PADDING.left}
                x2={PADDING.left + innerW}
                y1={y}
                y2={y}
                className="stroke-zinc-300 dark:stroke-zinc-700/60"
                strokeWidth={1}
                strokeDasharray={v === 0 ? undefined : "2 4"}
              />
              <text
                x={PADDING.left - 6}
                y={y + 3}
                fontSize="9"
                textAnchor="end"
                className="fill-zinc-500 font-mono dark:fill-zinc-600"
              >
                {axisFormatter(v)}
              </text>
            </g>
          );
        })}

        {/* bars */}
        {hours.map((hour, i) => {
          const slotX = PADDING.left + slot * i;
          const x = slotX + (slot - barWidth) / 2;
          const baseY = PADDING.top + innerH;

          // Stack layers bottom-up.
          let cursorY = baseY;
          const layers: Array<{ y: number; h: number; fill: string }> = [];
          for (const s of series) {
            const v = s.values[i] ?? 0;
            if (v <= 0) continue;
            const h = (v / niceMax) * innerH;
            cursorY -= h;
            layers.push({ y: cursorY, h, fill: s.fill });
          }

          const total = totals[i] ?? 0;
          const tipLines = [formatHourLabel(hour, true), `Total: ${valueFormatter(total)}`];
          for (let j = 0; j < series.length; j++) {
            tipLines.push(`${series[j]!.label}: ${valueFormatter(series[j]!.values[i] ?? 0)}`);
          }
          const tip = tipLines.join("\n");

          return (
            <g key={hour}>
              {total === 0 ? (
                <rect
                  x={x}
                  y={baseY - 1}
                  width={barWidth}
                  height={1}
                  className="fill-zinc-300 dark:fill-zinc-700/80"
                >
                  <title>{tip}</title>
                </rect>
              ) : (
                layers.map((l, idx) => (
                  <rect key={idx} x={x} y={l.y} width={barWidth} height={l.h} fill={l.fill} rx={1}>
                    <title>{tip}</title>
                  </rect>
                ))
              )}
            </g>
          );
        })}

        {/* x-axis labels — every 4th hour for legibility */}
        {hours.map((hour, i) => {
          if (i % 4 !== 0 && i !== hours.length - 1) return null;
          const slotX = PADDING.left + slot * i + slot / 2;
          return (
            <text
              key={hour}
              x={slotX}
              y={H - 8}
              fontSize="9"
              textAnchor="middle"
              className="fill-zinc-500 font-mono dark:fill-zinc-600"
            >
              {formatHourLabel(hour, false)}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

export function ChartLegend({ series }: { series: HourlySeries[] }) {
  return (
    <div className="flex flex-wrap items-center gap-3 text-[11.5px] text-zinc-600 dark:text-zinc-400">
      {series.map((s) => (
        <span key={s.label} className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.fill }} />
          {s.label}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------

function formatInt(n: number): string {
  return Math.round(n).toLocaleString("en-US");
}

function formatHourLabel(iso: string, full: boolean): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const hh = d.getHours().toString().padStart(2, "0");
  if (!full) return `${hh}:00`;
  return d.toLocaleString(undefined, {
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

function niceCeil(n: number): number {
  if (n <= 0) return 1;
  const exp = Math.floor(Math.log10(n));
  const base = Math.pow(10, exp);
  const norm = n / base;
  let nice: number;
  if (norm <= 1) nice = 1;
  else if (norm <= 2) nice = 2;
  else if (norm <= 5) nice = 5;
  else nice = 10;
  return nice * base;
}
